import { useCallback, useMemo } from 'react';
import { Button } from '@ds';
import { useLogisticsPermissions } from '@lp/features/RoutePlanner/hooks/useLogisticsPermissions';
import { useLogisticsActions } from '@lp/features/RoutePlanner/hooks/useLogisticsActions';
import { useQuoteDetails } from '@lp/features/RoutePlanner/hooks/useQuoteDetails';
import { useRouteModalActions } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { CaseStatus } from '@/api/apis/case';
import { isAllowedStatus } from '@/utils/helpers';

type LogisticsButtonsProps = {
  caseStatus: CaseStatus;
  isValidRoute: boolean;
};

export const LogisticsButtons = ({ caseStatus, isValidRoute }: LogisticsButtonsProps) => {
  const { onOpenModal, onCloseModal } = useRouteModalActions();
  const { openQuote } = useQuoteDetails();
  const { status, useFixedPriceFlow } = useCaseDetailsWithReviews();
  const {
    isSendToAviation,
    isAviationApprove,
    isSendQuoteInternal,
    isRoutePlanningDone,
    isReviewQuote,
    isGroundApprove,
    isBookResources,
    isSendQuoteToTx,
    isApproveQuoteByTXC,
    isBookingPendingStatus,
    isConfirmGroundDisabled,
  } = useLogisticsPermissions();
  const {
    sendToAviationApprove,
    handleAviationApprove,
    handleGroundApprove,
    handleCompleteRoute,
    bookResources,
    handleSendQuoteInternal,
    handleSendQuoteToTxReview,
    handleApproveQuoteByTXC,
    isLoading,
  } = useLogisticsActions();

  const isActiveCaseStatus = useMemo(() => {
    return isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });
  }, [status]);

  const handleSendToAviationApprove = useCallback(() => {
    onOpenModal({
      type: RouteModalTypes.QuoteComment,
      context: {
        title: 'Send for Aviation Approval',
        onAction: sendToAviationApprove,
        actionButtonTitle: 'Send for Aviation Approval',
        onClose: onCloseModal,
      },
    });
  }, [onOpenModal, sendToAviationApprove, onCloseModal]);

  const sendQuoteInternal = () => {
    onOpenModal({
      type: RouteModalTypes.QuoteComment,
      context: {
        title: 'Send for Quote Approval',
        onAction: handleSendQuoteInternal,
        actionButtonTitle: 'Send for Quote Approval',
        onClose: onCloseModal,
      },
    });
  };

  const sendToAviationApproveAction = useCallback(() => {
    return useFixedPriceFlow ? sendToAviationApprove() : handleSendToAviationApprove();
  }, [useFixedPriceFlow, sendToAviationApprove, handleSendToAviationApprove]);

  if (!isActiveCaseStatus) {
    return null;
  }

  if (isSendToAviation) {
    return (
      <Button size="sm" className="text-m-sb!" onPress={sendToAviationApproveAction} isLoading={isLoading}>
        Send for aviation approval
      </Button>
    );
  }

  if (isAviationApprove) {
    return (
      <Button size="sm" className="text-m-sb!" onPress={handleAviationApprove} isLoading={isLoading}>
        Approve aviation
      </Button>
    );
  }

  if (isSendQuoteInternal) {
    return (
      <Button size="sm" className="text-m-sb!" onPress={sendQuoteInternal} isLoading={isLoading}>
        Send quote for approval
      </Button>
    );
  }

  if (isRoutePlanningDone) {
    return (
      <Button size="sm" className="text-m-sb!" onPress={handleCompleteRoute} isLoading={isLoading}>
        Complete route planning
      </Button>
    );
  }

  if (isReviewQuote && ![CaseStatus.Finished, CaseStatus.Declined].includes(caseStatus)) {
    return (
      <Button
        size="sm"
        className="text-m-sb!"
        onPress={() => openQuote({ desyncedState: !isValidRoute })}
        isLoading={isLoading}
      >
        Review Quote
      </Button>
    );
  }

  if (isGroundApprove) {
    return (
      <Button
        size="sm"
        className="text-m-sb!"
        onPress={handleGroundApprove}
        isLoading={isLoading}
        isDisabled={isConfirmGroundDisabled}
      >
        Confirm ground
      </Button>
    );
  }

  if (isSendQuoteToTx) {
    return (
      <Button size="sm" className="text-m-sb!" onPress={handleSendQuoteToTxReview} isLoading={isLoading}>
        Send quote
      </Button>
    );
  }

  if (isApproveQuoteByTXC) {
    return (
      <Button size="sm" className="text-m-sb!" onPress={handleApproveQuoteByTXC} isLoading={isLoading}>
        Approve Quote by TX Center
      </Button>
    );
  }

  if (isBookResources) {
    return (
      <Button
        size="sm"
        className="text-m-sb!"
        isDisabled={isBookingPendingStatus}
        onPress={bookResources}
        isLoading={isLoading}
      >
        {isBookingPendingStatus ? 'Resources assigned' : 'Assign Resources'}
      </Button>
    );
  }

  return null;
};
